import React from 'react';
import { ReactComponent as IconWarning } from '@assets/Icons/Icon_Warning.svg';
import {
    useMaintenanceWindow,
    useFormatWindowStart,
} from '@shared/hooks/useMaintenanceStatus';

/**
 * Explains why the alerts page is quiet during a maintenance window
 * (CTX-548).
 *
 * The precise claim matters. Events keep being ingested and keep appearing in
 * the table below — the collector being under maintenance does not stop the
 * watcher, which is a separate feed. What stops is the outbound notification:
 * the backend does not hand alerts to notification-api while a window is
 * open, so nobody is emailed and nothing queues up to be delivered in a burst
 * afterwards.
 *
 * So this says "notifications are paused", not "monitoring is paused". The
 * difference is exactly what a reader needs to interpret the table: what is
 * on screen is real and current; what is missing is the email.
 */
const AlertingPausedNotice: React.FC = () => {
    const window = useMaintenanceWindow('Kontrol Edge');
    const formatWindowStart = useFormatWindowStart();

    if (!window) return null;

    const since = formatWindowStart(window.since);

    return (
        <div
            className="flex shrink-0 items-start gap-3 border-b border-semantic-border-primary bg-semantic-bg-warning-secondary px-4 py-3 text-semantic-text-warning"
            role="status"
        >
            <IconWarning className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="min-w-0">
                <p className="text-sm font-semibold">
                    Alert notifications are paused for maintenance.
                </p>
                <p className="mt-0.5 text-xs opacity-90">
                    {since
                        ? `Alerts raised since ${since} are still listed below, but we are not sending notifications until the window ends.`
                        : 'Alerts are still listed below, but we are not sending notifications until the window ends.'}{' '}
                    This is not an indication that your services are healthy or
                    unhealthy.
                </p>
            </div>
        </div>
    );
};

export default AlertingPausedNotice;
